import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { WorkExperienceSection } from '@/components/WorkExperienceSection';
import { Pencil, X, Check, Upload, Download, Trash2, Loader2, ExternalLink, ArrowLeft, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase-client';
import {
  calculateAggregatedMetrics,
  calculateWeeklyTrends,
  calculateRadarData,
  calculateBehaviourData,
  extractSkills,
  extractCoachingHighlights,
} from '@/lib/profile-metrics';

interface ProfileData {
  profile: {
    id: string;
    full_name: string;
    email: string;
    bio: string | null;
    job_title: string | null;
    company_name: string | null;
    location: string | null;
    timezone: string | null;
    target_role: string | null;
    linkedin_url: string | null;
    github_url: string | null;
    portfolio_url: string | null;
    resume_url: string | null;
    resume_filename: string | null;
    is_open_to_opportunities: boolean;
  };
  metrics: {
    overallScore: number;
    contentScore: number;
    behaviourScore: number;
    communication: number;
    confidence: number;
    problemSolving: number;
    technicalKnowledge: number;
    professionalism: number;
    experience: number;
    grade: string;
    totalInterviews: number;
    improvementPercentage: number;
  };
  weeklyTrends: Array<{
    week: string;
    contentScore: number;
    behaviourScore: number;
  }>;
  radarData: {
    structure: number;
    technicalDepth: number;
    clarity: number;
    relevance: number;
    detail: number;
  };
  behaviourData: {
    eyeContact: number;
    professionalism: number;
    pace: number;
    distractionFree: number;
  };
  skills: {
    technical: string[];
    soft: string[];
  };
  coachingHighlights: Array<{
    type: 'strength' | 'improvement';
    text: string;
  }>;
  latestSession: {
    title: string;
    company: string;
  } | null;
}

function ProfilePage() {
  const { user } = useAuth();
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedProfile, setEditedProfile] = useState<any>({});
  const [uploadingCV, setUploadingCV] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [viewedUserName, setViewedUserName] = useState<string>('');

  const contentRadarRef = useRef<HTMLCanvasElement>(null);
  const behaviourBarRef = useRef<HTMLCanvasElement>(null);
  const trendLineRef = useRef<HTMLCanvasElement>(null);
  const chartInstancesRef = useRef<any[]>([]);

  const isViewingOwnProfile = !userId || userId === user?.id;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
    script.async = true;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [userId, user]);

  const fetchProfileData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setPermissionError(false);
      const targetUserId = userId || user.id;
      console.log('[Profile] Fetching profile data for userId:', targetUserId);

      // Fetch the target user's profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', targetUserId)
        .maybeSingle();

      if (profileError) {
        console.error('[Profile] Error fetching profile:', profileError);
        setPermissionError(true);
        setLoading(false);
        return;
      }

      if (!profileData) {
        console.error('[Profile] No profile data returned - likely permission denied');
        setPermissionError(true);
        setLoading(false);
        return;
      }

      console.log('[Profile] Profile data received:', profileData);
      setViewedUserName(profileData.full_name);

      // Fetch all interview sessions for metrics calculation
      const { data: sessions, error: sessionsError } = await supabase
        .from('interview_sessions')
        .select(`
          *,
          job_posting:job_postings(id, title, company_name)
        `)
        .eq('user_id', targetUserId)
        .eq('status', 'completed')
        .order('ended_at', { ascending: false });

      if (sessionsError) {
        console.error('[Profile] Error fetching interview sessions:', sessionsError);
      }

      console.log('[Profile] Fetched sessions:', sessions);

      // Calculate metrics from interview sessions
      const metrics = calculateAggregatedMetrics(sessions || []);
      const weeklyTrends = calculateWeeklyTrends(sessions || []);
      const radarData = calculateRadarData(sessions || []);
      const behaviourData = calculateBehaviourData(sessions || []);
      const latestSession = sessions && sessions.length > 0 ? sessions[0] : null;
      const skills = extractSkills(latestSession);
      const coachingHighlights = extractCoachingHighlights(latestSession);

      // Transform data to match expected structure
      const transformedData = {
        profile: profileData,
        metrics,
        weeklyTrends,
        radarData,
        behaviourData,
        skills,
        coachingHighlights,
        latestSession: latestSession ? {
          title: latestSession.job_posting?.title || 'Interview',
          company: latestSession.job_posting?.company_name || 'Company',
        } : null,
      };

      console.log('[Profile] Transformed data with metrics:', transformedData);

      setProfileData(transformedData);
      setEditedProfile(transformedData.profile);
    } catch (error) {
      console.error('[Profile] Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) {
      console.error('[Profile] No user found');
      return;
    }

    setSaving(true);
    try {
      console.log('[Profile] Saving profile data:', editedProfile);

      // Prepare update data - only include fields that exist in user_profiles
      const updateData: any = {
        full_name: editedProfile.full_name,
        bio: editedProfile.bio || null,
        job_title: editedProfile.job_title || null,
        company_name: editedProfile.company_name || null,
        location: editedProfile.location || null,
        timezone: editedProfile.timezone || null,
        target_role: editedProfile.target_role || null,
        linkedin_url: editedProfile.linkedin_url || null,
        github_url: editedProfile.github_url || null,
        portfolio_url: editedProfile.portfolio_url || null,
        updated_at: new Date().toISOString(),
      };

      // Only include is_open_to_opportunities if it exists in editedProfile
      if ('is_open_to_opportunities' in editedProfile) {
        updateData.is_open_to_opportunities = editedProfile.is_open_to_opportunities;
      }

      console.log('[Profile] Update data:', updateData);

      const { data, error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('[Profile] Supabase error:', error);
        return;
      }

      console.log('[Profile] Save successful:', data);

      // Update local state immediately with the saved data
      if (data) {
        setProfileData(prev => prev ? {
          ...prev,
          profile: {
            ...prev.profile,
            ...data
          }
        } : null);
      }

      setEditMode(false);
    } catch (error: any) {
      console.error('[Profile] Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleOpportunities = async () => {
    if (!user) return;

    const newValue = !profileData?.profile.is_open_to_opportunities;
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ is_open_to_opportunities: newValue })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('[Profile] Error toggling opportunities:', error);
        return;
      }

      // Update local state immediately
      if (data) {
        setProfileData(prev => prev ? {
          ...prev,
          profile: {
            ...prev.profile,
            is_open_to_opportunities: data.is_open_to_opportunities
          }
        } : null);
      }
    } catch (error: any) {
      console.error('[Profile] Error toggling opportunities:', error);
    }
  };

  const handleCVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      console.error('[Profile] Invalid file type');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('[Profile] File too large');
      return;
    }

    setUploadingCV(true);
    try {
      const { uploadFile } = await import('@/lib/edge-functions');
      const { path, originalFilename } = await uploadFile(file, 'resumes', user.id);

      // Update profile with resume path and filename
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          resume_url: path,
          resume_filename: originalFilename
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('[Profile] Error updating resume URL:', error);
        return;
      }

      // Update local state
      if (data) {
        setProfileData(prev => prev ? {
          ...prev,
          profile: {
            ...prev.profile,
            resume_url: data.resume_url,
            resume_filename: data.resume_filename
          }
        } : null);
      }
    } catch (error) {
      console.error('[Profile] Error uploading CV:', error);
    } finally {
      setUploadingCV(false);
    }
  };

  const handleCVUploadFromFile = async (file: File) => {
    if (!user) return;

    if (file.size > 5 * 1024 * 1024) {
      console.error('[Profile] File too large');
      return;
    }

    setUploadingCV(true);
    try {
      const { uploadFile } = await import('@/lib/edge-functions');
      const { path, originalFilename } = await uploadFile(file, 'resumes', user.id);

      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          resume_url: path,
          resume_filename: originalFilename
        })
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        console.error('[Profile] Error updating resume URL:', error);
        return;
      }

      if (data) {
        setProfileData(prev => prev ? {
          ...prev,
          profile: {
            ...prev.profile,
            resume_url: data.resume_url,
            resume_filename: data.resume_filename
          }
        } : null);
      }
    } catch (error) {
      console.error('[Profile] Error uploading CV:', error);
    } finally {
      setUploadingCV(false);
    }
  };

  const handleCVDownload = async () => {
    if (!profileData?.profile.resume_url || !user) return;

    setUploadingCV(true);
    try {
      let filePath = profileData.profile.resume_url;

      // Extract file path if it's a full URL
      if (filePath.includes('/storage/v1/object/')) {
        const match = filePath.match(/\/resumes\/(.+?)(?:\?|$)/);
        if (match) {
          filePath = match[1];
        }
      }

      console.log('[Profile] Attempting to download file:', filePath);
      console.log('[Profile] Original filename:', profileData.profile.resume_filename);

      // Download directly from storage
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('resumes')
        .download(filePath);

      if (downloadError || !fileData) {
        console.error('[Profile] Error downloading file:', downloadError);
        return;
      }

      // Create a blob URL and download
      const url = URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = profileData.profile.resume_filename || 'resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[Profile] Error downloading CV:', error);
    } finally {
      setUploadingCV(false);
    }
  };

  const handleCVRemove = async () => {
    if (!user) return;
    setUploadingCV(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          resume_url: null,
          resume_filename: null
        })
        .eq('id', user.id);

      if (error) {
        console.error('[Profile] Error removing resume:', error);
        return;
      }

      setProfileData(prev => prev ? {
        ...prev,
        profile: {
          ...prev.profile,
          resume_url: null,
          resume_filename: null
        }
      } : null);
    } catch (error) {
      console.error('[Profile] Error removing CV:', error);
    } finally {
      setUploadingCV(false);
    }
  };

  useEffect(() => {
    const initCharts = () => {
      if (typeof window === 'undefined' || !(window as any).Chart || !profileData) return;

      const Chart = (window as any).Chart;

      chartInstancesRef.current.forEach(chart => {
        if (chart) chart.destroy();
      });
      chartInstancesRef.current = [];

      if (contentRadarRef.current) {
        const contentCtx = contentRadarRef.current.getContext('2d');
        if (contentCtx) {
          const chart = new Chart(contentCtx, {
            type: 'radar',
            data: {
              labels: ['Structure', 'Technical Depth', 'Clarity', 'Relevance', 'Detail'],
              datasets: [{
                label: 'Content',
                data: [
                  profileData.radarData.structure,
                  profileData.radarData.technicalDepth,
                  profileData.radarData.clarity,
                  profileData.radarData.relevance,
                  profileData.radarData.detail
                ],
                fill: true,
                backgroundColor: 'rgba(16, 185, 129, 0.12)',
                borderColor: 'rgba(16, 185, 129, 0.9)',
                pointBackgroundColor: 'rgba(16, 185, 129, 1)',
                pointBorderColor: '#ffffff'
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: {
                duration: 900,
                easing: 'easeOutQuart'
              },
              scales: {
                r: {
                  angleLines: { color: 'rgba(148, 163, 184, 0.4)' },
                  grid: { color: 'rgba(226, 232, 240, 1)' },
                  pointLabels: { color: '#0f172a', font: { size: 11 } },
                  ticks: { display: false, beginAtZero: true, max: 100 }
                }
              },
              plugins: {
                legend: { display: false }
              }
            }
          });
          chartInstancesRef.current.push(chart);
        }
      }

      if (behaviourBarRef.current) {
        const behaviourCtx = behaviourBarRef.current.getContext('2d');
        if (behaviourCtx) {
          const chart = new Chart(behaviourCtx, {
            type: 'bar',
            data: {
              labels: ['Eye Contact', 'Professionalism', 'Pace', 'Distraction-free'],
              datasets: [{
                label: 'Behaviour',
                data: [
                  profileData.behaviourData.eyeContact,
                  profileData.behaviourData.professionalism,
                  profileData.behaviourData.pace,
                  profileData.behaviourData.distractionFree
                ],
                backgroundColor: 'rgba(56, 189, 248, 0.7)',
                borderRadius: 8
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: {
                duration: 900,
                easing: 'easeOutQuart'
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { color: '#64748b', font: { size: 11 } }
                },
                y: {
                  grid: { color: 'rgba(226, 232, 240, 1)' },
                  ticks: {
                    color: '#64748b',
                    stepSize: 20,
                    callback: (v: any) => v
                  },
                  suggestedMin: 0,
                  suggestedMax: 100
                }
              },
              plugins: {
                legend: { display: false }
              }
            }
          });
          chartInstancesRef.current.push(chart);
        }
      }

      if (trendLineRef.current && profileData.weeklyTrends.length > 0) {
        const trendCtx = trendLineRef.current.getContext('2d');
        if (trendCtx) {
          const chart = new Chart(trendCtx, {
            type: 'line',
            data: {
              labels: profileData.weeklyTrends.map(t => t.week),
              datasets: [
                {
                  label: 'Content',
                  data: profileData.weeklyTrends.map(t => t.contentScore),
                  tension: 0.35,
                  borderWidth: 2,
                  borderColor: 'rgba(16, 185, 129, 1)',
                  pointRadius: 3,
                  pointBackgroundColor: 'rgba(16, 185, 129, 1)'
                },
                {
                  label: 'Behaviour',
                  data: profileData.weeklyTrends.map(t => t.behaviourScore),
                  tension: 0.35,
                  borderWidth: 2,
                  borderColor: 'rgba(56, 189, 248, 1)',
                  pointRadius: 3,
                  pointBackgroundColor: 'rgba(56, 189, 248, 1)'
                }
              ]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              animation: {
                duration: 1000,
                easing: 'easeOutQuart'
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: { color: '#64748b', font: { size: 11 } }
                },
                y: {
                  grid: { color: 'rgba(226, 232, 240, 1)' },
                  ticks: {
                    color: '#64748b',
                    stepSize: 10,
                    callback: (v: any) => v
                  },
                  suggestedMin: 40,
                  suggestedMax: 100
                }
              },
              plugins: {
                legend: {
                  labels: {
                    color: '#0f172a',
                    font: { size: 11 }
                  }
                }
              }
            }
          });
          chartInstancesRef.current.push(chart);
        }
      }
    };

    if ((window as any).Chart && profileData) {
      initCharts();
    } else {
      const checkChart = setInterval(() => {
        if ((window as any).Chart && profileData) {
          clearInterval(checkChart);
          initCharts();
        }
      }, 100);

      return () => {
        clearInterval(checkChart);
        chartInstancesRef.current.forEach(chart => {
          if (chart) chart.destroy();
        });
        chartInstancesRef.current = [];
      };
    }

    return () => {
      chartInstancesRef.current.forEach(chart => {
        if (chart) chart.destroy();
      });
      chartInstancesRef.current = [];
    };
  }, [profileData]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getGradeColor = (grade: string) => {
    if (grade === 'Strong') return 'emerald';
    if (grade === 'Developing') return 'sky';
    return 'slate';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (permissionError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 shadow-sm text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
            <p className="text-slate-600">
              You don't have permission to view this profile. Candidates are visible when they mark themselves as "Open to opportunities" or have interviewed for your organization's jobs.
            </p>
          </div>
          <Button
            onClick={() => navigate('/candidates')}
            className="w-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Candidates
          </Button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-600">Failed to load profile</p>
      </div>
    );
  }

  const { profile, metrics, skills, coachingHighlights, latestSession } = profileData;
  const gradeColor = getGradeColor(metrics.grade);

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <div className="min-h-screen">
          <div className="max-w-6xl mx-auto px-4 py-6 md:px-8 md:py-10 space-y-6 md:space-y-8">
            <header className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center font-semibold text-white shadow-sm">
                  {getInitials(profile.full_name)}
                </div>
                <div>
                  {editMode && isViewingOwnProfile ? (
                    <Input
                      value={editedProfile.full_name}
                      onChange={(e) => setEditedProfile({ ...editedProfile, full_name: e.target.value })}
                      className="text-lg md:text-4xl font-semibold h-auto p-1"
                    />
                  ) : (
                    <div>
                      <h1 className="text-lg md:text-4xl font-semibold text-slate-900">
                        {profile.full_name}
                      </h1>
                      {!isViewingOwnProfile && (
                        <p className="text-sm text-slate-500 mt-1">Viewing candidate profile</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {isViewingOwnProfile ? (
                  editMode ? (
                    <>
                      <Button
                        onClick={() => {
                          setEditMode(false);
                          setEditedProfile(profileData?.profile || editedProfile);
                        }}
                        variant="outline"
                        size="sm"
                        disabled={saving}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={handleSaveProfile}
                        size="sm"
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={() => setEditMode(true)}
                        variant="outline"
                        size="sm"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        onClick={toggleOpportunities}
                        size="sm"
                        variant={profile.is_open_to_opportunities ? "default" : "outline"}
                      >
                        {profile.is_open_to_opportunities ? 'Open to opportunities ✓' : 'Not seeking opportunities'}
                      </Button>
                    </>
                  )
                ) : (
                  <Button
                    onClick={() => navigate('/candidates')}
                    variant="outline"
                    size="sm"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Candidates
                  </Button>
                )}
              </div>
            </header>

            <section className="grid gap-4 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1.3fr)] items-start">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1 flex-1">
                    {editMode && isViewingOwnProfile ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="Job Title"
                          value={editedProfile.job_title || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, job_title: e.target.value })}
                        />
                        <Input
                          placeholder="Company Name"
                          value={editedProfile.company_name || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, company_name: e.target.value })}
                        />
                        <Input
                          placeholder="Location (e.g., Remote, EU timezone)"
                          value={editedProfile.location || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, location: e.target.value })}
                        />
                      </div>
                    ) : (
                      <>
                        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
                          {profile.job_title || profile.target_role || 'Add your job title'}
                        </h2>
                        {profile.company_name && (
                          <p className="text-sm font-semibold text-slate-800">{profile.company_name}</p>
                        )}
                        <p className="text-xs text-slate-500">
                          {profile.location || 'Add location'}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="text-right space-y-1">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Overall Interview Readiness
                    </p>
                    <span className={`inline-flex items-center gap-1 rounded-full bg-${gradeColor}-50 px-3 py-1 text-xs font-medium text-${gradeColor}-700 border border-${gradeColor}-100`}>
                      ● {metrics.grade}
                    </span>
                    <p className="text-[11px] text-slate-500">
                      Based on {metrics.totalInterviews} interview{metrics.totalInterviews !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {editMode && isViewingOwnProfile ? (
                  <Textarea
                    placeholder="Add a brief bio about yourself..."
                    value={editedProfile.bio || ''}
                    onChange={(e) => setEditedProfile({ ...editedProfile, bio: e.target.value })}
                    rows={3}
                  />
                ) : (
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {profile.bio || 'No bio provided.'}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {profile.is_open_to_opportunities && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      Open to new opportunities
                    </span>
                  )}
                  {metrics.improvementPercentage > 0 && (
                    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-slate-700">
                      ⭐ +{metrics.improvementPercentage}% improvement
                    </span>
                  )}
                </div>
              </div>

              <div className="grid gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        Pillar 1
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        Content & Technical
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1 rounded-full bg-${gradeColor}-50 px-2.5 py-1 text-[11px] font-medium text-${gradeColor}-700 border border-${gradeColor}-100`}>
                        ● {metrics.grade}
                      </span>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Score: {metrics.contentScore}/100
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex gap-1">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full ${
                            i < Math.round((metrics.contentScore / 100) * 8)
                              ? 'bg-emerald-500'
                              : 'bg-slate-200'
                          }`}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                        Pillar 2
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        Behaviour & Professionalism
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700 border border-sky-100">
                        ● {metrics.grade}
                      </span>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Score: {metrics.behaviourScore}/100
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex gap-1">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full ${
                            i < Math.round((metrics.behaviourScore / 100) * 8)
                              ? 'bg-sky-500'
                              : 'bg-slate-200'
                          }`}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <WorkExperienceSection
                resumePath={profileData.profile.resume_url}
                resumeFilename={profileData.profile.resume_filename}
                onResumeUpload={handleCVUploadFromFile}
                onResumeDownload={handleCVDownload}
                onResumeRemove={handleCVRemove}
                uploadingCV={uploadingCV}
                userId={userId || user?.id || ''}
                isReadOnly={!isViewingOwnProfile}
              />
            </section>

            <section className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                      Pillar 1
                    </p>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Content & Technical Breakdown
                    </h3>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700 border border-emerald-100 font-medium">
                    {metrics.grade}
                  </span>
                </div>
                <div className="h-40">
                  <canvas ref={contentRadarRef}></canvas>
                </div>
                {metrics.totalInterviews === 0 && (
                  <p className="mt-3 text-xs text-slate-500 text-center">
                    Complete your first interview to see metrics
                  </p>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                      Pillar 2
                    </p>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Behaviour & Professionalism
                    </h3>
                  </div>
                  <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] text-sky-700 border border-sky-100 font-medium">
                    {metrics.grade}
                  </span>
                </div>
                <div className="h-40">
                  <canvas ref={behaviourBarRef}></canvas>
                </div>
                {metrics.totalInterviews === 0 && (
                  <p className="mt-3 text-xs text-slate-500 text-center">
                    Complete your first interview to see metrics
                  </p>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                      Pillar 3
                    </p>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Improvement Over Time
                    </h3>
                  </div>
                  {metrics.improvementPercentage > 0 && (
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] text-emerald-700 border border-emerald-100 font-medium">
                      +{metrics.improvementPercentage}%
                    </span>
                  )}
                </div>
                <div className="h-40">
                  <canvas ref={trendLineRef}></canvas>
                </div>
                {profileData.weeklyTrends.length === 0 && (
                  <p className="mt-3 text-xs text-slate-500 text-center">
                    Complete more interviews to see trends
                  </p>
                )}
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Recent Feedback
                    </h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    {coachingHighlights.length === 0 ? (
                      <p className="text-xs text-slate-500">Complete an interview to receive feedback</p>
                    ) : (
                      coachingHighlights.map((highlight, idx) => (
                        <div
                          key={idx}
                          className={`rounded-xl ${
                            highlight.type === 'strength'
                              ? 'bg-emerald-50 border border-emerald-100'
                              : 'bg-sky-50 border border-sky-100'
                          } px-3 py-2.5`}
                        >
                          <p className={`text-[11px] uppercase tracking-[0.25em] ${
                            highlight.type === 'strength' ? 'text-emerald-700' : 'text-sky-700'
                          } mb-1`}>
                            {highlight.type === 'strength' ? 'Strength' : 'Improvement'}
                          </p>
                          <p className="text-slate-800 text-xs">{highlight.text}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Skills & Focus Areas
                    </h3>
                    <span className="text-[11px] rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700 border border-emerald-100">
                      From latest interview
                    </span>
                  </div>
                  <div className="space-y-3 text-xs md:text-sm">
                    {skills.technical.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                          Technical Skills
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.technical.map((skill, idx) => (
                            <span key={idx} className="rounded-full bg-slate-100 px-2.5 py-1 border border-slate-200 text-slate-800">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {skills.soft.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">
                          Soft Skills
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.soft.map((skill, idx) => (
                            <span key={idx} className="rounded-full bg-slate-100 px-2.5 py-1 border border-slate-200 text-slate-800">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {skills.technical.length === 0 && skills.soft.length === 0 && (
                      <p className="text-xs text-slate-500">Complete an interview to extract skills</p>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Resume & Links
                  </h3>
                  <div className="space-y-3 text-sm">
                    {editMode && isViewingOwnProfile ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="LinkedIn URL"
                          value={editedProfile.linkedin_url || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, linkedin_url: e.target.value })}
                        />
                        <Input
                          placeholder="GitHub URL"
                          value={editedProfile.github_url || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, github_url: e.target.value })}
                        />
                        <Input
                          placeholder="Portfolio URL"
                          value={editedProfile.portfolio_url || ''}
                          onChange={(e) => setEditedProfile({ ...editedProfile, portfolio_url: e.target.value })}
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {profile.resume_url ? (
                          <div className="space-y-2">
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2">
                              <p className="text-xs text-emerald-600 font-medium">Resume uploaded</p>
                              <p className="text-xs text-emerald-800 truncate mt-0.5">
                                {profile.resume_filename || 'resume.pdf'}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleCVDownload}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download CV
                              </Button>
                              {isViewingOwnProfile && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handleCVRemove}
                                  disabled={uploadingCV}
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                >
                                  {uploadingCV ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : isViewingOwnProfile ? (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={handleCVUpload}
                              disabled={uploadingCV}
                              className="hidden"
                            />
                            <Button
                              size="sm"
                              className="w-full pointer-events-none bg-[#16a34a] hover:bg-[#15803d] text-white"
                              disabled={uploadingCV}
                            >
                              {uploadingCV ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              {uploadingCV ? 'Uploading...' : 'Upload CV'}
                            </Button>
                          </label>
                        ) : (
                          <p className="text-xs text-slate-500 py-2">No resume uploaded</p>
                        )}
                        <div className="grid gap-2 md:grid-cols-2">
                          {profile.linkedin_url ? (
                            <a
                              href={profile.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-slate-800 hover:bg-slate-200 transition text-xs"
                            >
                              <span className="flex items-center gap-2">
                                🔗 LinkedIn
                              </span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : null}
                          {profile.github_url ? (
                            <a
                              href={profile.github_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-slate-800 hover:bg-slate-200 transition text-xs"
                            >
                              <span className="flex items-center gap-2">
                                🔗 GitHub
                              </span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : null}
                          {profile.portfolio_url ? (
                            <a
                              href={profile.portfolio_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-between rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-slate-800 hover:bg-slate-200 transition text-xs"
                            >
                              <span className="flex items-center gap-2">
                                🔗 Portfolio
                              </span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : null}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <footer className="pt-2 pb-4 text-xs text-slate-500 flex flex-wrap justify-between gap-2 border-t border-slate-200 mt-2">
              <p>Interview Trainer · Bias-aware candidate insights.</p>
              <p>Scores are guidance only.</p>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
}

export default function ProfilePageWrapper() {
  return (
    <ProtectedRoute>
      <ProfilePage />
    </ProtectedRoute>
  );
}
